import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Timer, CookingPot, Motorcycle, Package, XCircle, Wallet as WalletIcon, ArrowCounterClockwise } from '@phosphor-icons/react';
import { trackOrder, getOrderDetail, cancelOrder } from '../../services/customerApi';
import { PageWrap } from './CustomerApp';
import { toast } from 'sonner';

const STATUS_STEPS = {
  DELIVERY: [
    { status: 'NEW', label: 'Order Placed', icon: CheckCircle, color: '#2E7D32' },
    { status: 'PREPARING', label: 'Preparing', icon: CookingPot, color: '#F57C00' },
    { status: 'READY_DELIVERY', label: 'Ready', icon: Package, color: '#E21836' },
    { status: 'RIDER_ASSIGNED', label: 'Rider Assigned', icon: Motorcycle, color: '#E21836' },
    { status: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', icon: Motorcycle, color: '#E21836' },
    { status: 'DELIVERED', label: 'Delivered', icon: CheckCircle, color: '#2E7D32' },
  ],
  TAKEAWAY: [
    { status: 'NEW', label: 'Order Placed', icon: CheckCircle, color: '#2E7D32' },
    { status: 'PREPARING', label: 'Preparing', icon: CookingPot, color: '#F57C00' },
    { status: 'READY_TAKEAWAY', label: 'Ready for Pickup', icon: Package, color: '#E21836' },
    { status: 'PICKED_UP', label: 'Picked Up', icon: CheckCircle, color: '#2E7D32' },
  ],
  DINE_IN: [
    { status: 'NEW', label: 'Order Placed', icon: CheckCircle, color: '#2E7D32' },
    { status: 'PREPARING', label: 'Preparing', icon: CookingPot, color: '#F57C00' },
    { status: 'READY_DINEIN', label: 'Ready to Serve', icon: Package, color: '#E21836' },
    { status: 'SERVED', label: 'Served', icon: CheckCircle, color: '#2E7D32' },
  ],
};

function getStatusIndex(orderType, currentStatus) {
  const steps = STATUS_STEPS[orderType] || STATUS_STEPS.DELIVERY;
  // Map READY to the appropriate ready status
  let mappedStatus = currentStatus;
  if (currentStatus === 'READY') {
    if (orderType === 'DELIVERY') mappedStatus = 'READY_DELIVERY';
    else if (orderType === 'TAKEAWAY') mappedStatus = 'READY_TAKEAWAY';
    else mappedStatus = 'READY_DINEIN';
  }
  const idx = steps.findIndex((s) => s.status === mappedStatus);
  return idx >= 0 ? idx : 0;
}

export default function CustomerTrack() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelPicker, setShowCancelPicker] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadOrder();
    const interval = setInterval(loadOrder, 10000);
    return () => clearInterval(interval);
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      let res;
      if (token) {
        res = await getOrderDetail(orderId);
      } else {
        res = await trackOrder(orderId);
      }
      setOrder(res.data);
    } catch {
      // Try public track
      try {
        const res = await trackOrder(orderId);
        setOrder(res.data);
      } catch {
        setOrder(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF9]">
        <div className="w-8 h-8 border-3 border-[#E21836] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFDF9] px-6">
        <XCircle size={48} className="text-[#D32F2F] mb-4" />
        <h2 className="text-xl font-bold text-[#2D231E]">Order Not Found</h2>
        <button onClick={() => navigate('/order')} className="mt-4 text-sm text-[#E21836] font-semibold">
          Go Home
        </button>
      </div>
    );
  }

  const orderType = order.order_type || 'DELIVERY';
  const steps = STATUS_STEPS[orderType] || STATUS_STEPS.DELIVERY;
  const currentIndex = getStatusIndex(orderType, order.status);
  const isCancelled = order.status === 'CANCELLED';
  const canSelfCancel = order.status === 'NEW';

  const handleCancel = async (refundMethod) => {
    setCancelling(true);
    try {
      await cancelOrder(order.id, refundMethod);
      toast.success('Order cancelled');
      setShowCancelPicker(false);
      await loadOrder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel order');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <PageWrap>
      <div className="min-h-screen pb-24" data-testid="track-page">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-[#FFFDF9]/80 backdrop-blur-xl border-b border-[#E8E2D6]/50 px-4 py-3">
          <div className="flex items-center gap-3">
            <button data-testid="track-back-btn" onClick={() => navigate('/order/orders')} className="p-1">
              <ArrowLeft size={20} className="text-[#2D231E]" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-[#2D231E]">Order #{order.order_number}</h2>
              <p className="text-xs text-[#6C5D53]">{orderType.replace('_', ' ')} &middot; {order.items?.length || 0} items</p>
            </div>
          </div>
        </div>

        <div className="px-4 pt-6">
          {isCancelled ? (
            <div className="flex items-center gap-3 p-4 bg-[#D32F2F]/10 border border-[#D32F2F]/20 rounded-2xl mb-6">
              <XCircle size={28} weight="fill" className="text-[#D32F2F]" />
              <div>
                <h3 className="font-bold text-[#D32F2F]">Order Cancelled</h3>
                <p className="text-xs text-[#6C5D53]">This order has been cancelled</p>
              </div>
            </div>
          ) : (
            /* Status Timeline */
            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6C5D53] mb-4">Order Status</h3>
              <div className="space-y-0">
                {steps.map((step, i) => {
                  const isCompleted = i <= currentIndex;
                  const isActive = i === currentIndex;
                  const Icon = step.icon;
                  return (
                    <div key={step.status} className="flex gap-3" data-testid={`status-step-${step.status}`}>
                      <div className="flex flex-col items-center">
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: i * 0.1 }}
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isCompleted
                              ? 'bg-[#2E7D32]'
                              : 'bg-[#E8E2D6]'
                          } ${isActive ? 'ring-4 ring-[#2E7D32]/20' : ''}`}
                        >
                          <Icon size={16} weight="fill" className={isCompleted ? 'text-white' : 'text-[#6C5D53]'} />
                        </motion.div>
                        {i < steps.length - 1 && (
                          <div className={`w-0.5 h-8 ${i < currentIndex ? 'bg-[#2E7D32]' : 'bg-[#E8E2D6]'}`} />
                        )}
                      </div>
                      <div className="pb-6">
                        <p className={`text-sm font-semibold ${isCompleted ? 'text-[#2D231E]' : 'text-[#6C5D53]'}`}>
                          {step.label}
                        </p>
                        {isActive && (
                          <p className="text-xs text-[#E21836] mt-0.5 flex items-center gap-1">
                            <Timer size={12} /> In progress...
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Self-cancel — only while order is still NEW */}
          {canSelfCancel && !showCancelPicker && (
            <button
              onClick={() => setShowCancelPicker(true)}
              className="w-full mb-6 py-3 rounded-xl border border-[#D32F2F]/30 text-[#D32F2F] text-sm font-semibold flex items-center justify-center gap-2"
            >
              <XCircle size={18} weight="bold" />
              Cancel this order
            </button>
          )}

          {canSelfCancel && showCancelPicker && (
            <div className="mb-6 p-4 bg-[#F6F3EC] border border-[#E8E2D6] rounded-2xl">
              <h3 className="text-sm font-bold text-[#2D231E] mb-1">Cancel order?</h3>
              <p className="text-xs text-[#6C5D53] mb-4">This can't be undone. Choose how you'd like your refund.</p>
              <div className="space-y-2">
                <button
                  disabled={cancelling}
                  onClick={() => handleCancel('original')}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E8E2D6] bg-white text-left"
                >
                  <ArrowCounterClockwise size={20} className="text-[#2D231E]" />
                  <div>
                    <p className="text-sm font-semibold text-[#2D231E]">Refund to original payment method</p>
                    <p className="text-[11px] text-[#6C5D53]">Back to your card/UPI, a few business days</p>
                  </div>
                </button>
                <button
                  disabled={cancelling}
                  onClick={() => handleCancel('wallet')}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E8E2D6] bg-white text-left"
                >
                  <WalletIcon size={20} className="text-[#4a6741]" />
                  <div>
                    <p className="text-sm font-semibold text-[#2D231E]">Credit to wallet</p>
                    <p className="text-[11px] text-[#6C5D53]">Instant — use it on your next order</p>
                  </div>
                </button>
              </div>
              <button
                disabled={cancelling}
                onClick={() => setShowCancelPicker(false)}
                className="w-full mt-3 py-2 text-xs text-[#6C5D53]"
              >
                Never mind, keep my order
              </button>
            </div>
          )}

          {/* Items */}
          <div className="p-4 bg-[#F6F3EC] border border-[#E8E2D6] rounded-2xl mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6C5D53] mb-3">Items Ordered</h3>
            {(order.items || []).map((item, i) => (
              <div key={i} className="flex justify-between text-sm mb-1">
                <span className="text-[#6C5D53]">{item.menu_item_name} x{item.qty}</span>
                <span className="text-[#2D231E]">&#8377;{(item.price * item.qty).toFixed(0)}</span>
              </div>
            ))}
            <div className="border-t border-[#E8E2D6] mt-2 pt-2 flex justify-between">
              <span className="font-bold text-[#2D231E]">Total</span>
              <span className="font-bold text-[#E21836]">&#8377;{(order.total || 0).toFixed(0)}</span>
            </div>
          </div>

          {/* Auto-refresh note */}
          <p className="text-center text-xs text-[#6C5D53]">
            <Timer size={12} className="inline" /> Auto-refreshes every 10 seconds
          </p>
        </div>
      </div>
    </PageWrap>
  );
}
